import { DownloadManagerAction, DownloadManagerActionKeys } from './actions';
import { MultiAppAuthenticationInfosAction, MultiAppAuthenticationInfosActionKeys } from '../MultiAppAuthentication/actions';
import Utils from '../../utils';
import { isSessionCancelled } from '../../utils/sessionCancellation';

export interface DownloadManagerState {
	downloadGroups: DownloadGroups;
	isDeletingAll: boolean;
	isDownloadToastClosed: boolean;
	isInitReconcileInProgress: boolean;
	isMarkingItemForDeletion: boolean;
	items: { [k: number]: DownloadManagerItemState };
	pendingSelectedDownloads: { [k: number]: SelectedDownloadsManagerItemState };
	totalSelectedDownloadsCount: number;
	downloadInProgressItems: number[];
	downloadInProgressDependencies: number[];
}

const getInitialState = (): DownloadManagerState => ({
	downloadGroups: getInitialDownloadGroups(),
	isDeletingAll: false,
	isDownloadToastClosed: false,
	isInitReconcileInProgress: false,
	isMarkingItemForDeletion: false,
	items: {},
	pendingSelectedDownloads: {},
	totalSelectedDownloadsCount: 0,
	downloadInProgressItems: [],
	downloadInProgressDependencies: [],
});

const getInitialDownloadGroups = (): DownloadGroups => ({
	all: getInitialDownloadGroup(),
	important: getInitialDownloadGroup(),
	selected: getInitialDownloadGroup(),
});

const getInitialDownloadGroup = (): DownloadGroup => ({
	isDownloading: false,
	status: DownloadGroupStatus.NotStarted,
});

export enum DownloadGroupName {
	ALL = 'all',
	IMPORTANT = 'important',
	SELECTED = 'selected',
}

export enum DownloadGroupStatus {
	NotStarted = 'notStarted',
	Started = 'started',
	Finished = 'finished'
}

export interface Progress {
	totalSize?: number;
	loaded?: number;
}

export enum DownloadMode {
	Temp = 'temporary',			// Needed for Api.DriveItemType.File on Android
	Requested = 'requested',	// When the user explicitly download the item
	Dependency = 'dependency',	// Dependency of a Requested, Required or Temp
	Required = 'required',		// For items in Menu that needs to be download on initialization for offline support
	Unspecified = 'unspecified' // For cases where the download mode is not defined
}

export enum DownloadStatus {
	NOT_DOWNLOADED = 'NOT_DOWNLOADED',
	INIT = 'INIT',
	GETTING_DEPENDENCIES = 'GETTING_DEPENDENCIES',
	NATIVE_DOWNLOADING = 'NATIVE_DOWNLOADING',
	NATIVE_DOWNLOADED = 'NATIVE_DOWNLOADED',
	ERROR = 'ERROR',
	UPDATING = 'UPDATING',
	STARTING_UPDATE = 'STARTING_UPDATE',
	STARTING_DOWNLOAD = 'STARTING_DOWNLOAD',
	RESUMING_DOWNLOAD = 'RESUMING_DOWNLOAD',
	STARTING_DELETION = 'STARTING_DELETION',
}

export interface DownloadGroups {
	all: DownloadGroup;
	important: DownloadGroup;
	selected: DownloadGroup;
}

export interface DownloadGroup {
	status: DownloadGroupStatus;
	isDownloading: boolean;
}

export interface DownloadManagerItemState {
	mode: DownloadMode;
	status: DownloadStatus;
	progress?: Progress;
	version: number;
	requestId?: number;
	dependencies: number[];
	dependents: number[];
	dependenciesResolved: boolean;
	downloadConfirmed: boolean;
}

export interface SelectedDownloadsManagerItemState {
	driveItemId: number;
}

const downloadManagerItemInitialState: DownloadManagerItemState = {
	mode: DownloadMode.Unspecified,
	status: DownloadStatus.NOT_DOWNLOADED,
	version: -1,
	dependencies: [],
	dependents: [],
	dependenciesResolved: false,
	downloadConfirmed: false,
};

export function downloadManager(state: DownloadManagerState = getInitialState(), action: DownloadManagerAction | MultiAppAuthenticationInfosAction): DownloadManagerState {

	if (action.type === MultiAppAuthenticationInfosActionKeys.LOGOUT) {
		return getInitialState();
	}

	if (isSessionCancelled() && Utils.stringIsInStringEnum(action.type, DownloadManagerActionKeys)) {
		return state;
	}

	let newState = Utils.deepCopy(state);
	let item: DownloadManagerItemState;

	switch (action.type) {
		case DownloadManagerActionKeys.SET_DOWNLOAD_GROUP_STATUS:
			newState.downloadGroups[action.group].status = action.status;
			newState.downloadGroups[action.group].isDownloading = action.status === DownloadGroupStatus.Started;
			if (action.status === DownloadGroupStatus.Started) {
				newState.isDownloadToastClosed = false;
			}
			return newState;

		case DownloadManagerActionKeys.SET_DELETE_ALL_STATUS:
			newState.isDeletingAll = action.isDeletingAll;
			return newState;

		case DownloadManagerActionKeys.SET_IS_MARKING_ITEM_FOR_DELETION:
			newState.isMarkingItemForDeletion = action.isMarkingItemForDeletion;
			return newState;

		case DownloadManagerActionKeys.INIT_PREVIOUS_DOWNLOAD_GROUPS:
			newState.downloadGroups.all.status = action.downloadGroupStatus;
			newState.downloadGroups.all.isDownloading = false;
			newState.downloadGroups.important.status = action.downloadImportantStatus;
			newState.downloadGroups.important.isDownloading = false;
			newState.downloadGroups.selected.status = action.downloadSelectedStatus;
			newState.downloadGroups.selected.isDownloading = false;
			return newState;

		case DownloadManagerActionKeys.INIT_DOWNLOADS:
			action.downloads.forEach(item => newState.items[item.id] = {
				mode: item.mode,
				status: item.status,
				version: item.version,
				dependencies: item.dependencies,
				dependents: item.dependents,
				dependenciesResolved: item.dependenciesResolved !== undefined
					? item.dependenciesResolved
					: item.dependencies.length > 0,
				downloadConfirmed: item.downloadConfirmed === true,
			});
			return newState;

		case DownloadManagerActionKeys.INIT_SELECTED_DOWNLOADS:
			action.pendingSelectedDownloads.forEach((item: SelectedDownloadsManagerItemState) => {
				if (item && item.driveItemId) {
					newState.pendingSelectedDownloads[item.driveItemId] = {
						driveItemId: item.driveItemId,
					};
				}
			});
			newState.totalSelectedDownloadsCount = action.pendingSelectedDownloads.length;
			return newState;

		case DownloadManagerActionKeys.ADD_ITEM:
			let newItem: DownloadManagerItemState = getItemFromMap(newState.items, action.id);
			newItem.mode = action.mode;
			newItem.dependencies = action.dependencies;
			newItem.dependents = action.dependents;
			newItem.version = action.version;
			newItem.status = DownloadStatus.INIT;
			newItem.dependenciesResolved = action.dependenciesResolved;
			newItem.downloadConfirmed = false;
			newState.items[action.id] = newItem;
			return newState;

		case DownloadManagerActionKeys.ADD_SELECTED_DOWNLOAD_ITEM:
			if (newState.pendingSelectedDownloads[action.driveItemId]) {
				return state;
			}

			let newPendingSelectedDownloadItem: SelectedDownloadsManagerItemState = getSelectedDownloadItemFromMap(newState.pendingSelectedDownloads, action.driveItemId);
			newPendingSelectedDownloadItem.driveItemId = action.driveItemId;
			newState.pendingSelectedDownloads[action.driveItemId] = newPendingSelectedDownloadItem;
			newState.totalSelectedDownloadsCount = state.totalSelectedDownloadsCount + 1;
			return newState;

		case DownloadManagerActionKeys.ADD_DOWNLOAD_IN_PROGRESS_ITEM:
			if (!newState.downloadInProgressItems.includes(action.driveItemId)) {
				newState.downloadInProgressItems = [...newState.downloadInProgressItems, action.driveItemId];
			}
			return newState;

		case DownloadManagerActionKeys.ADD_DOWNLOAD_IN_PROGRESS_DEPENDENCY:
			if (!newState.downloadInProgressDependencies.includes(action.dependencyId)) {
				newState.downloadInProgressDependencies = [...newState.downloadInProgressDependencies, action.dependencyId];
			}
			return newState;

		case DownloadManagerActionKeys.UPDATE_STATUS:
			item = getItemFromMap(newState.items, action.id);
			item.status = action.status;
			if ([
				DownloadStatus.NATIVE_DOWNLOADED,
				DownloadStatus.STARTING_DOWNLOAD,
				DownloadStatus.STARTING_UPDATE,
				DownloadStatus.RESUMING_DOWNLOAD,
			].includes(action.status)) {
				item.downloadConfirmed = false;
			}
			newState.items[action.id] = item;
			return newState;

		case DownloadManagerActionKeys.SET_REQUEST_ID:
			item = getItemFromMap(newState.items, action.id);
			item.requestId = action.requestId;
			item.status = action.isUpdating ? DownloadStatus.UPDATING : DownloadStatus.NATIVE_DOWNLOADING;
			newState.items[action.id] = item;
			return newState;

		case DownloadManagerActionKeys.RECORD_DOWNLOAD_PROGRESS:
			item = getItemFromMap(newState.items, action.id);
			item.progress = action.progress;
			newState.items[action.id] = item;
			return newState;

		case DownloadManagerActionKeys.UPDATE_DOWNLOAD:
			item = getItemFromMap(newState.items, action.id);
			item.mode = action.mode;
			item.dependencies = action.dependencies;
			item.dependents = action.dependents;
			item.version = action.version;
			if (action.dependenciesResolved !== undefined) {
				item.dependenciesResolved = action.dependenciesResolved;
			}
			if (action.downloadConfirmed !== undefined) {
				item.downloadConfirmed = action.downloadConfirmed;
			}
			newState.items[action.id] = item;
			return newState;

		case DownloadManagerActionKeys.REMOVE_DOWNLOAD:
			delete newState.items[action.id];
			return newState;
		case DownloadManagerActionKeys.REMOVE_PENDING_SELECTED_DOWNLOAD_ITEM:
			delete newState.pendingSelectedDownloads[action.driveItemId];
			return newState;
		case DownloadManagerActionKeys.REMOVE_DOWNLOAD_IN_PROGRESS_ITEM:
			newState.downloadInProgressItems = newState.downloadInProgressItems.filter(id => id !== action.driveItemId);
			return newState;
		case DownloadManagerActionKeys.REMOVE_DOWNLOAD_IN_PROGRESS_DEPENDENCY:
			newState.downloadInProgressDependencies = newState.downloadInProgressDependencies.filter(id => id !== action.dependencyId);
			return newState;
		case DownloadManagerActionKeys.RESET_TOTAL_SELECTED_DOWNLOAD_ITEMS:
			newState.totalSelectedDownloadsCount = 0;
			return newState;
		case DownloadManagerActionKeys.RESET_PENDING_SELECTED_DOWNLOAD_ITEMS:
			newState.pendingSelectedDownloads = {};
			return newState;
		case DownloadManagerActionKeys.CLOSE_DOWNLOAD_GROUP_TOAST:
			newState.isDownloadToastClosed = true;
			return newState;
		case DownloadManagerActionKeys.SET_INIT_RECONCILE_IN_PROGRESS:
			newState.isInitReconcileInProgress = action.inProgress;
			return newState;
		case DownloadManagerActionKeys.RESET_DOWNLOADS: {
			const initial = getInitialState();
			return {
				...state,
				downloadGroups: initial.downloadGroups,
				items: initial.items,
				pendingSelectedDownloads: initial.pendingSelectedDownloads,
				totalSelectedDownloadsCount: initial.totalSelectedDownloadsCount,
				downloadInProgressItems: initial.downloadInProgressItems,
				downloadInProgressDependencies: initial.downloadInProgressDependencies,
				isInitReconcileInProgress: initial.isInitReconcileInProgress,
			};
		}
		default:
			return state;
	}
}

function getItemFromMap(items: { [k: number]: DownloadManagerItemState }, itemId: number): DownloadManagerItemState {
	return items[itemId] ? items[itemId] : Utils.deepCopy(downloadManagerItemInitialState);
}

function getSelectedDownloadItemFromMap(selectedDownloads: { [k: number]: SelectedDownloadsManagerItemState }, itemId: number): SelectedDownloadsManagerItemState {
	return selectedDownloads[itemId] ? selectedDownloads[itemId] : { driveItemId: itemId };
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/containers/DownloadManager/reducer.ts